import type { CartV2 } from '~/types'

export interface PriceableItem {
  listPrice?: number | { amount?: number }
  scalePrices?: Array<{ minQuantity: number, price: number | { amount?: number } }>
  quantity: number
  campaign?: { afterPrice?: number, status?: string }
  isActiveCampaign?: boolean
}

export interface EffectivePriceScaleInput {
  minQuantity: number
  price: number | { amount?: number }
  reference?: string | null
  isIndividualPrice?: boolean
  isGpoPrice?: boolean
}

export interface EffectivePriceProductInput {
  listPrice?: number | { amount?: number }
  scalePrices?: EffectivePriceScaleInput[]
  campaign?: { status?: string, afterPrice?: number }
  isActiveCampaign?: boolean
}

function extractCents(value: number | { amount?: number } | undefined): number {
  if (typeof value === 'number') return value
  if (value && typeof value === 'object' && 'amount' in value) return value.amount ?? 0
  return 0
}

/**
 * Resolves the effective per-unit price in cents for a product at a given
 * quantity and customer reference. Mirrors the `getBestPrice` logic in
 * `useShoppingListActions` so GTM tracking uses the same tier as the UI.
 */
export function getEffectiveScalePriceCents(
  product: EffectivePriceProductInput,
  quantity: number = 1,
  reference?: string,
): number {
  const listPrice = extractCents(product.listPrice)
  const scalePrices = product.scalePrices ?? []

  let applicablePrice = listPrice

  if (scalePrices.length > 0) {
    const applicable = scalePrices
      .filter(scale =>
        quantity >= scale.minQuantity
        && (!scale.reference || !reference || scale.reference.toLowerCase() === reference.toLowerCase()),
      )
      .sort((a, b) => b.minQuantity - a.minQuantity)[0]

    if (applicable) {
      applicablePrice = extractCents(applicable.price)
    }
  }

  const isActiveCampaign = product.isActiveCampaign === true || product.campaign?.status === 'ACTIVE'
  const campaignAfterPrice = product.campaign?.afterPrice

  if (isActiveCampaign && typeof campaignAfterPrice === 'number' && campaignAfterPrice < applicablePrice) {
    return campaignAfterPrice
  }

  return applicablePrice
}

export function usePriceUtils() {
  function getItemPrice(item: PriceableItem): number {
    const scalePrices = Array.isArray(item.scalePrices) ? item.scalePrices : []
    const basePrice = extractCents(item.listPrice)

    // find the applicable scale price (INDIVIDUAL, GPO, SUPPLIER, etc.)
    let applicablePrice = basePrice

    // scale prices
    if (scalePrices.length > 0) {
      const applicable = scalePrices
        .filter(scale => item.quantity >= scale.minQuantity)
        .sort((a, b) => b.minQuantity - a.minQuantity)[0]
      if (applicable)
        applicablePrice = extractCents(applicable.price)
    }

    // Check for campaign price
    const campaignAfterPrice = item.campaign?.afterPrice
    const isActiveCampaign = item.isActiveCampaign ?? item.campaign?.status === 'ACTIVE'

    // Only return campaign price if it's cheaper than the applicable price
    if (isActiveCampaign && typeof campaignAfterPrice === 'number' && campaignAfterPrice < applicablePrice) {
      return campaignAfterPrice
    }
    return applicablePrice
  }

  // Calculate the total price with scale prices by customer reference
  function calculateTotalByReference(cart: CartV2, referenceTag: string): number {
    return cart.items
      .filter(item => item.reference === referenceTag)
      .reduce((total, item) => total + getItemPrice(item) * item.quantity, 0)
  }

  // Calculate the total price for a whole cart (without reference tags)
  function calculateCartTotal(cart: CartV2): number {
    return cart.items.reduce((total, item) => total + getItemPrice(item) * item.quantity, 0)
  }

  // Calculate freight cost by reference tag
  function getFreightCostByReference(cart: CartV2, referenceTag: string): number {
    const totalSum = calculateTotalByReference(cart, referenceTag)
    const freightAllowance = typeof cart.supplier?.freightAllowance === 'object'
      ? cart.supplier?.freightAllowance?.amount
      : cart.supplier?.freightAllowance || Number.POSITIVE_INFINITY
    const freightCharge = typeof cart.supplier?.freightCharge === 'object'
      ? cart.supplier.freightCharge.amount
      : cart.supplier?.freightCharge || 0

    return totalSum >= freightAllowance ? 0 : freightCharge
  }

  // General freight cost calculation
  function getFreightCost(cart: CartV2): number {
    const totalSum = calculateCartTotal(cart)
    const freightAllowance = typeof cart.supplier?.freightAllowance === 'object'
      ? cart.supplier?.freightAllowance?.amount
      : cart.supplier?.freightAllowance || Number.POSITIVE_INFINITY
    const freightCharge = typeof cart.supplier?.freightCharge === 'object'
      ? cart.supplier.freightCharge.amount
      : cart.supplier?.freightCharge || 0

    return totalSum >= freightAllowance ? 0 : freightCharge
  }

  return {
    getItemPrice,
    calculateTotalByReference,
    calculateCartTotal,
    getFreightCostByReference,
    getFreightCost,
  }
}
